import { UNCONFIRMED_SIGN_UP_EXISTS_ERROR } from "@/utils/cognitoErrors"
import type {
  ConfirmSignUpOutput,
  ResendSignUpCodeOutput,
  SignInOutput,
  SignUpOutput,
} from "aws-amplify/auth"

// A Microsoft signup is only auto-confirmed when the pre-signup trigger can
// verify the email from the Microsoft token. Otherwise Cognito sends a
// verification code and the user must confirm it on the Authenticator's
// confirm-signup screen before the Microsoft sign-in can complete.
interface PendingMicrosoftSignUp {
  username: string
  idToken: string
  // Code already sent by signUp; handed to the confirm screen once instead of
  // sending a second mail.
  sentCode?: ResendSignUpCodeOutput
  // The next Authenticator sign-in submit for this user opens the confirm screen.
  routeToConfirm: boolean
}

let pending: PendingMicrosoftSignUp | null = null

export type MicrosoftSignUpResult = "SIGNED_IN" | "CONFIRM_SIGN_UP"

const hasErrorName = (error: unknown, name: string): boolean =>
  typeof error === "object" &&
  error !== null &&
  "name" in error &&
  (error as { name?: unknown }).name === name

export async function signUpOrSignInWithMicrosoft({
  username,
  idToken,
  signUp,
  authenticate,
}: {
  username: string
  idToken: string
  signUp: () => Promise<SignUpOutput>
  authenticate: () => Promise<unknown>
}): Promise<MicrosoftSignUpResult> {
  pending = null

  try {
    const result = await signUp()
    if (!result.isSignUpComplete) {
      pending = {
        username,
        idToken,
        sentCode:
          result.nextStep.signUpStep === "CONFIRM_SIGN_UP"
            ? result.nextStep.codeDeliveryDetails
            : undefined,
        routeToConfirm: true,
      }
      return "CONFIRM_SIGN_UP"
    }
  } catch (error) {
    if (!hasErrorName(error, "UsernameExistsException")) throw error
  }

  try {
    await authenticate()
  } catch (error) {
    if (!hasErrorName(error, "UserNotConfirmedException")) throw error
    // The unconfirmed account was not created by this attempt: whoever
    // registered it may know its password. Confirming it here would hand them
    // the account once the code from this user's mailbox is entered, so the
    // confirm screen is only ever opened for a signUp made just above.
    throw Object.assign(
      new Error("An unconfirmed account already exists for this email"),
      { name: UNCONFIRMED_SIGN_UP_EXISTS_ERROR, cause: error },
    )
  }
  return "SIGNED_IN"
}

// Authenticator handleSignIn hook: answer the sign-in submit issued after an
// unconfirmed Microsoft signup with CONFIRM_SIGN_UP, so the Authenticator
// opens its own confirm-signup screen for this user.
export function takeMicrosoftConfirmSignUpStep(
  username: string,
): SignInOutput | null {
  if (!pending?.routeToConfirm || pending.username !== username) return null
  pending.routeToConfirm = false
  return { isSignedIn: false, nextStep: { signInStep: "CONFIRM_SIGN_UP" } }
}

// Authenticator handleResendSignUpCode hook: the confirm screen requests a code
// on entry. Reuse the one signUp just sent; later requests really resend.
export function resendMicrosoftSignUpCode(
  username: string,
  resend: () => Promise<ResendSignUpCodeOutput>,
): Promise<ResendSignUpCodeOutput> {
  if (pending?.username === username && pending.sentCode) {
    const sentCode = pending.sentCode
    pending.sentCode = undefined
    return Promise.resolve(sentCode)
  }
  return resend()
}

// Authenticator handleConfirmSignUp hook: once the code is confirmed, finish
// the Microsoft sign-in with the token from the original attempt.
export async function completeMicrosoftSignUp(
  username: string,
  result: ConfirmSignUpOutput,
  authenticate: (idToken: string) => Promise<unknown>,
): Promise<void> {
  if (pending?.username !== username || !result.isSignUpComplete) return
  const { idToken } = pending
  pending = null
  await authenticate(idToken)
}
